import React, { useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { ArrowLeft, Scale } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

// "FOR PAUL" (ordernow1021d2, Paul 21 Oct 2026; his 2 Oct 2026 rule: price disputes and credit / refund claims go to him
// before ANYTHING is sent to a supplier). OWNER ONLY (the server's require_stock_owner!: admin user or the owner PIN).
// Each row: the evidence (PO, invoice + PDF or filed path, the price ordered vs invoiced, the delivery) and his mark:
// "Approved to raise" or "Drop", with his typed name (one event; a later mark corrects it, nothing is deleted).
// There is NO send button on this page: "Approved to raise" only unlocks the query text on the order round, for staff
// to send. Phone width (390px) first.

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

const INPUT = 'w-full rounded-xl border border-brand-border bg-white px-3 py-2 text-base text-brand-ink focus:border-brand-primary focus:outline-none'

function when(iso) {
  return iso ? String(iso).slice(0, 16).replace('T', ' ') : ''
}

export default function ForPaul({ disputes = [], default_name = '' }) {
  const [name, setName] = useState(default_name || '')
  const open = disputes.filter((d) => !d.decision)
  const decided = disputes.filter((d) => d.decision)
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
        <Link href="/stock_takes" className="mb-3 inline-flex items-center gap-1 text-sm text-brand-muted"><ArrowLeft size={14} /> Stock takes</Link>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-brand-ink"><Scale size={18} /> For Paul: price differences and credit claims</h1>
        <p className="mt-1 text-xs text-brand-muted">Nothing goes to a supplier until you approve. Approving only lets staff send the query from the order round; nothing is sent from this page. Prices per box, ex VAT.</p>
        <label className="mt-3 block text-xs font-medium text-brand-muted">Your name (stamped on your decision)
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={cn(INPUT, 'mt-1 sm:max-w-xs')} />
        </label>
        <h2 className="mt-5 text-sm font-semibold text-brand-ink">To decide ({open.length})</h2>
        {open.length === 0 && <p className="mt-2 text-sm text-brand-muted">Nothing waiting.</p>}
        {open.map((d) => <DisputeCard key={d.key} d={d} name={name} />)}
        {decided.length > 0 && <h2 className="mt-6 text-sm font-semibold text-brand-ink">Decided ({decided.length})</h2>}
        {decided.map((d) => <DisputeCard key={d.key} d={d} name={name} />)}
      </div>
    </DashboardLayout>
  )
}

function DisputeCard({ d, name }) {
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)
  async function decide(decision) {
    if (inFlight.current || !name.trim()) return
    inFlight.current = true
    setBusy(true)
    try {
      const res = await fetch('/stock/for_paul/decide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        credentials: 'same-origin',
        body: JSON.stringify({ key: d.key, decision, name, note }),
      })
      let data = {}
      try { data = await res.json() } catch (_) { data = {} }
      if (res.ok && data.ok) toast.success(data.note || 'Saved')
      else toast.error(data.error || 'Not saved')
    } catch (_) {
      toast.error('Not saved: no connection')
    }
    setBusy(false)
    inFlight.current = false
    router.reload({ preserveScroll: true })
  }
  const dec = d.decision
  return (
    <div className="mt-3 rounded-2xl border border-brand-border bg-white p-4 text-sm" data-testid="for-paul-row">
      <p className="font-semibold text-brand-ink">{{ price_difference: 'Price difference', credit_claim: 'Credit claim', unallocated_charge: 'Charge not on our order' }[d.kind] || d.kind} · R{d.amount} ex VAT</p>
      <p className="text-xs text-brand-muted">{d.supplier_name} · PO {d.po_number}{d.supplier_order_number ? ` · their order ${d.supplier_order_number}` : ''}</p>
      <p className="mt-1 text-brand-ink">{d.code ? `${d.code} ` : ''}{d.item}</p>
      <p className="text-xs text-brand-ink">{d.text}</p>
      {d.line_id && <p className="text-xs text-brand-muted">Ordered {d.ordered_packs} boxes of {d.pack_size} at R{d.ordered_price} · received {d.received_packs} boxes</p>}
      {(d.delivery || []).map((x, i) => <p key={`d${i}`} className="text-xs text-brand-muted">Delivery note {x.note_number} ({x.delivered_on}): {x.packs} boxes</p>)}
      {(d.invoices || []).map((v, i) => (
        <p key={`i${i}`} className="text-xs text-brand-muted">Invoice {v.number} ({v.date}){v.whole_order ? `: total R${v.total_ex_vat} ex VAT` : `: ${v.packs ?? '-'} boxes at R${v.price ?? '-'}`}
          {v.pdf_url ? <a href={v.pdf_url} target="_blank" rel="noreferrer" className="ml-1 text-brand-primary underline">PDF</a>
            : (v.pdf_path ? <span className="ml-1 break-all">{v.pdf_path}</span> : null)}
        </p>
      ))}
      {dec && (
        <p className={cn('mt-2 rounded-xl px-3 py-2 text-xs font-medium', dec.decision === 'approved' ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-100 text-brand-muted')}>
          {dec.decision === 'approved' ? 'Approved to raise' : 'Dropped'} by {dec.by} {when(dec.at)}{dec.note ? `: ${dec.note}` : ''}
        </p>
      )}
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={cn(INPUT, 'mt-2 text-sm')} />
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => decide('approved')} disabled={busy || !name.trim() || (dec && dec.decision === 'approved')}
          className="rounded-xl bg-brand-primary px-4 py-2 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">Approved to raise</button>
        <button type="button" onClick={() => decide('dropped')} disabled={busy || !name.trim() || (dec && dec.decision === 'dropped')}
          className="rounded-xl border border-brand-border bg-white px-4 py-2 text-sm font-semibold text-brand-ink disabled:text-brand-muted">Drop</button>
      </div>
      {!name.trim() && <p className="mt-1 text-xs text-brand-muted">Type your name above first.</p>}
    </div>
  )
}
